import { GetStaticPropsResult } from 'next'
import React, { ReactElement, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { PageMeta } from 'src/pages'
import {
  useCreateBookingMutationMutation,
  useGetProjectionsForMovieQuery,
} from 'types/types'
import { ArrowForwardOutline } from 'react-ionicons'
import homeData from '@/content/home.json'
import { findCinema } from '@/src/cinemas'
import { Cinema, Movie, Projection } from '@/src/types'
import { findMovieByProjection, getAllProjections } from '@/src/movies'
import { Layout } from '@/components/Layout'
import { Input } from '@/components/elements/Input'
import { Checkbox } from '@/components/elements/Checkbox'
import SubmitButton from '@/components/elements/Button/submit'
import { Select } from '@/components/elements/Select'
import { Container } from '@/components/utils'
import { Alert } from '@/components/elements/Alert'
import { formatDateTime } from '@/utils/formatDate'
import {
  NewsletterSection,
  NewsletterSectionProps,
} from '@/components/patterns/NewsletterSection'
import { Dialog } from '@/components/elements/Alert/dialog'
import { Link } from '@/components/elements/Link'
import { IonIcon } from '@/src/icons'
import { scrollIntoViewWithOffset } from '../../utils/scrollIntoViewWithOffset'

interface ProjectionPageProps {
  // eslint-disable-next-line react/no-unused-prop-types
  meta: PageMeta
  cinema: Cinema
  movie: Movie
  newsletter: NewsletterSectionProps
  projection: Projection
  cinemaOptions: SelectOptions[]
  sessionOptions: SelectOptions[]
}

interface SelectOptions {
  name: string
  value: string
}

export async function getStaticPaths() {
  const projections = await getAllProjections()

  return {
    paths: projections.map((projection) => ({
      params: { id: projection.id },
    })),
    fallback: false,
  }
}

export async function getStaticProps({
  params,
}): Promise<GetStaticPropsResult<ProjectionPageProps>> {
  const movie = await findMovieByProjection(params.id)

  const projection = movie.projections.find((p) => p.id === params.id)

  const cinema = findCinema(projection.cinema)

  if (cinema) {
    const cinemaOptions = [
      { name: `${cinema.name} - ${cinema.city}`, value: cinema.id },
    ]
    const allCinemaSessions = movie.projections.filter((session) => {
      return session.cinema === cinema.id
    })
    const sessionOptions = allCinemaSessions.map((session) => {
      const date = formatDateTime(new Date(session.date).toString())
      return { name: date, value: session.id }
    })

    return {
      props: {
        meta: {
          title: `${movie.title} a ${cinema.name}`,
          description: '',
        },
        ...homeData,
        cinema,
        movie,
        projection,
        cinemaOptions,
        sessionOptions,
      },
    }
  }
  return {
    props: {
      meta: {
        title: `${movie.title}`,
        description: '',
      },
      ...homeData,
      cinema: null,
      movie,
      projection,
      cinemaOptions: null,
      sessionOptions: null,
    },
  }
}

export default function ProjectionPage({
  meta,
  cinema,
  newsletter,
  movie,
  projection,
  cinemaOptions,
  sessionOptions,
}: ProjectionPageProps): ReactElement {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm()

  const {
    data,
    loading: loadingMovie,
    error: errorLoading,
  } = useGetProjectionsForMovieQuery({
    variables: {
      movieId: movie.id,
    },
    onCompleted: (movieData) => {
      if (movieData.movie) {
        changeSession(projection.id)
      }
    },
  })
  //console.log(errorLoading)
  const [errorMessages, setErrorMessages] = useState('')
  const [createBooking, bookingState] = useCreateBookingMutationMutation({
    onError: ({ networkError, graphQLErrors }) => {
      if (networkError) {
        setErrorMessages(
          "S'ha produït un error de conexió amb la base de dades"
        )
      }
      if (graphQLErrors) {
        setErrorMessages(graphQLErrors[0].message)
      }
    },
  })

  const { data: bookingResponseData, loading, error } = bookingState

  const submitBooking = (formData) => {
    const { name, surname, email, phone, numberOfTickets, session } = formData
    createBooking({
      variables: {
        createBookingData: {
          projectionId: session,
          name,
          surname,
          phone,
          email,
          numberOfTickets: parseInt(numberOfTickets, 10),
          emailValidated: false,
        },
      },
    })
  }

  const texts = {
    title: "Reserva les entrades per l'espectacle",
    subtitle:
      "Omple els camps del següent formulari per bloquejar-te places per a l'esdeveniment",
    ticketsInputLabel: "Nombre d'entrades",
    ticketsInputDescription: 'El màxim de reserves permès és de 5.',
  }

  const [ticketOptions, setAvailableSeats] = useState([1])
  const [pageTexts, setPageTexts] = useState(texts)
  const changeSession = (projectionID) => {
    if (!data?.movie) {
      return
    }
    const selectedProjection = data.movie.projections.find(
      (p) => p.id === projectionID
    )
    const availableSeats = Math.min(
      selectedProjection.numberOfAvailableSeats,
      5
    )

    //console.log(availableSeats)
    texts.ticketsInputDescription =
      availableSeats >= 5
        ? 'El màxim de reserves permès és de 5.'
        : `Només queden ${availableSeats} entrades disponibles per aquesta sessió.`

    setPageTexts(texts)

    setAvailableSeats(Array.from({ length: availableSeats }, (_, i) => i + 1))
  }
  useEffect(() => {
    changeSession(projection.id)
  }, [])

  useEffect(() => {
    changeSession(projection.id)
  }, [data])


  const ticketOptionsLabel = (ticketNumber) => {
    return ticketNumber === 1 ? '1 entrada' : `${ticketNumber} entrades`
  }

  const scrollToTopOfProjectionPage = () => {
    setTimeout(() => {
      scrollIntoViewWithOffset(
        document.getElementById('booking-container'),
        -200
      )
    }, 100)
    return true
  }

  return cinema ? (
    <Layout meta={meta}>
      <div className="py-12 bg-beige" id="booking-container">
        <Container>
          {!bookingResponseData && (
            <div>
              <div className="mx-auto pt-8 max-w-md text-center">
                <h1 className="text-2xl">{pageTexts.title}</h1>
                <p className="py-4 font-body text-base leading-6">
                  {pageTexts.subtitle}
                </p>
              </div>
              <form
                onSubmit={handleSubmit(submitBooking)}
                className="mb-4 mx-auto px-4 py-4 max-w-lg text-navy-900 font-body bg-white rounded-lg md:px-10 md:py-10"
              >
                <Input
                  id="first-name"
                  label="Nom"
                  name="name"
                  register={register}
                  required
                />
                <Input
                  className="mt-4"
                  id="surname"
                  label="Cognoms"
                  name="surname"
                  register={register}
                  required
                />
                <Input
                  className="mt-4"
                  id="email-input"
                  label="Correu electrònic"
                  name="email"
                  register={register}
                  required
                  type="email"
                />
                <Input
                  className="mt-4"
                  id="phone-input"
                  label="Telèfon"
                  name="phone"
                  register={register}
                  required
                />
                <Select
                  className="mt-4"
                  options={cinemaOptions.map((cinemaOption) => ({
                    label: cinemaOption.name,
                    value: cinemaOption.value,
                  }))}
                  id="cinema"
                  label="Sala"
                  name="cinema"
                  register={register}
                  required
                  disabled
                />
                <Select
                  className="mt-4"
                  options={sessionOptions.map((session) => ({
                    label: session.name,
                    value: session.value,
                  }))}
                  id="session"
                  label="Sessió"
                  defaultValue={projection.id}
                  name="session"
                  register={register}
                  required
                  onChange={(e) => {
                    changeSession(e.target.value)
                  }}
                />
                <div>
                  <span className="block mt-4 font-semibold">
                    {pageTexts.ticketsInputLabel}
                  </span>
                  <span className="block text-navy-100 text-sm">
                    {pageTexts.ticketsInputDescription}
                  </span>
                  {ticketOptions.map((i) => (
                    <Checkbox
                      key={`tickets-${i}`}
                      className="mt-4"
                      name="numberOfTickets"
                      type="radio"
                      register={register}
                      id={`tickets-${i}`}
                      value={i}
                      label={ticketOptionsLabel(i)}
                      required
                    />
                  ))}
                </div>
                <Checkbox
                  name="acceptPrivacy"
                  register={register}
                  id="privacy-policy"
                  label="Accepto la política de privacitat"
                  description={<PrivacyPolicy />}
                  className="mt-12"
                  required
                />

                <div className="mt-8">
                  <SubmitButton
                    className="px-6 w-full md:w-auto"
                    icon={<IonIcon icon={ArrowForwardOutline} />}
                    label="Enviar formulari"
                    isLoading={loading}
                    disabled={ticketOptions.length <= 0}
                  />
                </div>
                {error && (
                  <Alert
                    type="error"
                    className="mt-6"
                    title="Error en el formulari"
                    description={errorMessages}
                  />
                )}
              </form>
            </div>
          )}
          {bookingResponseData && scrollToTopOfProjectionPage() && (
            <Dialog
              title="Formulari enviat!"
              description="Revisa la teva safata d’entrada per confirmar la teva reserva. Gràcies pel teu compromís amb el català!"
              type="success"
            />
          )}
        </Container>
      </div>
      <NewsletterSection {...newsletter} />
    </Layout>
  ) : null
}

export const PrivacyPolicy = (): JSX.Element => {
  return (
    <ul className="mt-2 text-navy-100">
      <li>
        <strong>Responsable: </strong>
        Òmnium Cultural.
      </li>
      <li>
        <strong>Finalitats: </strong>
        Gestionar la teva inscripció al projecte Cinexic i fer-te arribar
        informació del projecte i d’Òmnium Cultural.
      </li>
      <li>
        <strong>Drets: </strong>
        Accés, rectificació, supressió, oposició, limitació i portabilitat de
        dades.
      </li>
      <li>
        <strong>Més informació: </strong>
        Trobaràs informació addicional i més detallada a la{' '}
        <Link
          href="/politica-de-privacitat"
          className="text-navy-100"
          variant="no-variant"
          target="_blank"
        >
          Política de Privacitat
        </Link>
        .
      </li>
    </ul>
  )
}
